import React, { useMemo } from 'react';
import { Box, Typography, Divider, CircularProgress, Alert, Stack, Paper } from '@mui/material';
import { useQuery, gql } from '@apollo/client';
import { CreateWidgetForm } from './CreateWidgetForm';
import { WidgetListEditor } from './WidgetListEditor';
import DashboardsSection from './DashboardsSection';
import { SourceField, FieldDefinition } from '../types';

const GET_WIDGETS_AND_SECTIONS = gql`
  query GetDashboardWidgetsAndSections {
    getDashboardWidgets {
      id
      title
      fieldId
      sectionId
      chartType
      aggregation
      layout {
        x
        y
        w
        h
        minW
        minH
      }
      visible
      orderIndex
      isSystem
      systemKey
    }
    getCustomFieldSections {
      id
      title
      fields {
        id
        name
        type
        options
        published
      }
    }
  }
`;

const buildSourceFields = (sections: any[]): SourceField[] => {
  const fields: SourceField[] = [];
  sections.forEach((section) => {
    (section.fields || []).filter((f: any) => f.published !== false).forEach((field: any) => {
      fields.push({
        fieldId: field.id,
        label: `${section.title} › ${field.name}`,
        fieldType: field.type,
      });
    });
  });
  return fields;
};

export const DashboardTab: React.FC = () => {
  const { data, loading, error } = useQuery(GET_WIDGETS_AND_SECTIONS, {
    fetchPolicy: 'cache-and-network',
  });

  const widgets = data?.getDashboardWidgets || [];
  const sourceFields = useMemo(() => buildSourceFields(data?.getCustomFieldSections || []), [data]);

  const previewFieldsMap = useMemo(() => {
    const map = new Map<string, FieldDefinition>();
    (data?.getCustomFieldSections || []).forEach((section: any) => {
      (section.fields || []).forEach((field: any) => {
        map.set(field.id, { id: field.id, name: field.name, type: field.type, options: field.options });
      });
    });
    return map;
  }, [data]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={5}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Dashboards
          </Typography>
          <DashboardsSection />
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Widget Library
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            These charts are available to assign to any dashboard slot. Click the eye icon to preview a chart.
          </Typography>
          {sourceFields.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No custom fields available yet. Create some in the “Custom Data Fields” tab to build custom dashboard widgets. Your system widgets (Habit Consistency, Tasks, etc.) are already available below.
            </Alert>
          ) : (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <CreateWidgetForm sourceFields={sourceFields} />
            </Paper>
          )}

          <WidgetListEditor widgets={widgets} sourceFields={sourceFields} previewFields={previewFieldsMap} />
        </Box>
      </Stack>
    </Box>
  );
};

export default DashboardTab;
